/** The item editor: one form for single, multi, numeric, short-text, ordering and hotspot
 * items (STORY-041: ordering lists the correct order, students see it shuffled; hotspot uses
 * labelled regions, one image per region). Validation lives in the service; a refused save shows
 * the service's message on the field it names. */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { capabilities } from "../platform/capabilities";
import { awaitGeneration, generateItems, isOfflineError, type Item, type ItemIn, type LO } from "./api";
import { DraftChip } from "../ui";
import { Button } from "../ui";

const TYPES = ["single", "multi", "numeric", "short_text", "ordering", "hotspot"] as const;
const MAX_IMAGE_BYTES = 300_000;
const BLOOM = ["remember", "understand", "apply", "analyse", "evaluate", "create"] as const;
const KEYS = ["A", "B", "C", "D", "E", "F"];

type Option = { key: string; label: string; correct: boolean; misconception: string; image?: string | null };

export type EditorValue = Omit<ItemIn, "options"> & { options: Option[] };

function fromItem(it: Item | null, lang: string): EditorValue {
  if (!it) {
    return {
      type: "single",
      stem: "",
      lo_id: null,
      options: [
        { key: "A", label: "", correct: true, misconception: "" },
        { key: "B", label: "", correct: false, misconception: "" },
        { key: "C", label: "", correct: false, misconception: "" },
        { key: "D", label: "", correct: false, misconception: "" },
      ],
      answer_spec: {},
      confidence_enabled: true,
      spoken: false,
      bloom: null,
      difficulty_authored: null,
      tags: [],
      lang,
    };
  }
  return {
    type: it.type as EditorValue["type"],
    stem: it.stem,
    lo_id: it.lo_id ?? null,
    options: (it.options ?? []).map((o) => ({
      key: String(o.key ?? ""),
      label: String(o.label ?? ""),
      correct: !!o.correct,
      misconception: String(o.misconception ?? ""),
      image: (o as { image?: string | null }).image ?? null,
    })),
    answer_spec: it.answer_spec ?? {},
    confidence_enabled: it.confidence_enabled ?? true,
    spoken: it.spoken ?? false,
    bloom: it.bloom ?? null,
    difficulty_authored: it.difficulty_authored ?? null,
    tags: it.tags ?? [],
    lang: it.lang,
  };
}

function toBody(v: EditorValue): ItemIn {
  const hasOptions = v.type === "single" || v.type === "multi" || v.type === "ordering" || v.type === "hotspot";
  const kept = v.options.filter((o) => o.label.trim() !== "" || (v.type === "hotspot" && o.image));
  return {
    ...v,
    options: hasOptions
      ? kept.map((o) => ({
          key: o.key,
          label: o.label.trim(),
          correct: v.type === "ordering" ? false : o.correct,
          misconception: v.type === "ordering" ? null : o.misconception.trim() || null,
          ...(v.type === "hotspot" ? { image: o.image ?? null } : {}),
        }))
      : [],
    answer_spec: v.type === "numeric" ? v.answer_spec : v.type === "ordering" ? { order: kept.map((o) => o.key) } : {},
  };
}

export function ItemEditor({
  item,
  los,
  lang,
  courseId,
  defaultLoId,
  aiOff = false,
  reviewStatus = null,
  onReview,
  onSave,
  onCancel,
}: {
  item: Item | null;
  los: LO[];
  lang: string;
  courseId?: string;
  /** Where this item stands in review; null where there is nothing to approve yet. */
  reviewStatus?: string | null;
  /** The teacher's decision on the item: approved items may be asked in class, drafts may not. */
  onReview?: (status: "draft" | "approved") => Promise<void>;
  /** Drafting needs a server or a local model; the hand-written path stays open either way. */
  aiOff?: boolean;
  /** Preselected objective for a new item (an ad hoc question during a lesson, STORY-032). */
  defaultLoId?: string | null;
  onSave: (body: ItemIn) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [v, setV] = useState<EditorValue>(() => ({ ...fromItem(item, lang), ...(item || !defaultLoId ? {} : { lo_id: defaultLoId }) }));
  const [listening, setListening] = useState<null | (() => void)>(null);
  const [dictateNote, setDictateNote] = useState<string | null>(null);
  const dictation = capabilities.dictation;
  const toggleDictation = () => {
    if (listening) {
      listening();
      setListening(null);
      return;
    }
    if (!dictation) return;
    const before = v.stem;
    setDictateNote(dictation.processor === "browser-vendor" ? t("editor.dictateVendor") : null);
    const stop = dictation.start(
      lang.startsWith("ru") ? "ru-RU" : "en-US",
      (text) => setV((s) => ({ ...s, stem: (before ? `${before} ` : "") + text })),
      (err) => {
        setDictateNote(t("editor.dictateFailed", { error: err }));
        setListening(null);
      },
    );
    setListening(() => stop);
  };
  const [error, setError] = useState<{ field?: string; detail: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const draftItem = reviewStatus === "draft";
  async function review(status: "draft" | "approved") {
    if (!onReview) return;
    setReviewing(true);
    setError(null);
    try {
      await onReview(status);
    } catch (e) {
      setError({ detail: isOfflineError(e) ? t("editor.failed") : e instanceof ApiError ? e.detail : t("editor.failed") });
    } finally {
      setReviewing(false);
    }
  }
  const set = <K extends keyof EditorValue>(k: K, val: EditorValue[K]) => setV((s) => ({ ...s, [k]: val }));
  const setOpt = (i: number, patch: Partial<Option>) =>
    setV((s) => ({ ...s, options: s.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) }));
  const markCorrect = (i: number, on: boolean) =>
    setV((s) => ({
      ...s,
      options: s.options.map((o, j) => (v.type === "single" || v.type === "hotspot" ? { ...o, correct: j === i } : j === i ? { ...o, correct: on } : o)),
    }));
  const [drafting, setDrafting] = useState(false);
  const [aiNote, setAiNote] = useState<string | null>(null);
  const spec = v.answer_spec as { value?: number; tolerance?: number };

  async function draftWithAi() {
    if (!courseId || !v.lo_id) return;
    setDrafting(true);
    setAiNote(null);
    try {
      const started = await generateItems(courseId, { lo_id: v.lo_id, count: 1, types: [v.type], bloom: v.bloom, materialise: false });
      const g = await awaitGeneration(courseId, started.job_id);
      const d = g.drafts[0];
      if (g.status !== "done" || !d) {
        setAiNote(t("editor.aiFailed", { error: g.error ?? "" }));
        return;
      }
      setV((s) => ({
        ...s,
        stem: d.stem,
        options: (d.options ?? []).map((o) => ({ key: String(o.key ?? ""), label: String(o.label ?? ""), correct: !!o.correct, misconception: String(o.misconception ?? "") })),
        answer_spec: d.answer_spec ?? {},
        bloom: d.bloom ?? s.bloom,
        difficulty_authored: d.difficulty_authored ?? s.difficulty_authored,
        tags: d.tags ?? s.tags,
      }));
      setAiNote(t("editor.aiDrafted"));
    } catch (e) {
      setAiNote(isOfflineError(e) ? t("plan.needsAi") : t("editor.aiFailed", { error: String(e) }));
    } finally {
      setDrafting(false);
    }
  }
  const fieldError = (name: string) => (error?.field === name ? error.detail : null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await onSave(toBody(v));
    } catch (e) {
      if (isOfflineError(e)) setError({ detail: t("editor.failed") });
      else if (e instanceof ApiError) {
        const field = (e.body as { field?: string } | undefined)?.field;
        setError({ field, detail: e.detail });
      } else setError({ detail: t("editor.failed") });
    } finally {
      setBusy(false);
    }
  }

  const input = "field";
  return (
    <form
      className="flex flex-col gap-4 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      {item && reviewStatus && onReview && (
        <div className="flex flex-wrap items-center gap-3 border-b pb-3 text-sm" style={{ borderColor: "var(--line)" }}>
          {draftItem ? <DraftChip kind={item.source === "ai" ? "draft" : "edited"} /> : <span className="text-[color:var(--ok)]">✓ {t("items.approved")}</span>}
          <span className="flex-1 text-[color:var(--ink-2)]">{draftItem ? t("items.approval.editorDraft") : t("items.approval.editorApproved")}</span>
          <Button variant={draftItem ? "secondary" : "tertiary"} loading={reviewing} onClick={() => void review(draftItem ? "approved" : "draft")}>
            {draftItem ? t("items.approval.approve") : t("items.approval.toDraft")}
          </Button>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-3">
        <label className="flex flex-col gap-1 text-sm">
          {t("editor.type")}
          <select className={input} value={v.type} onChange={(e) => set("type", e.target.value as EditorValue["type"])} disabled={!!item}>
            {TYPES.map((x) => (
              <option key={x} value={x}>{t(`editor.types.${x}`)}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          {t("editor.objective")}
          <select className={input} value={v.lo_id ?? ""} onChange={(e) => set("lo_id", e.target.value || null)}>
            <option value="">{t("editor.noObjective")}</option>
            {los.map((lo) => (
              <option key={lo.lo_id} value={lo.lo_id}>{lo.code} · {lo.text}</option>
            ))}
          </select>
        </label>
        {courseId && !item && (
          <div className="flex flex-col justify-end gap-1 text-sm">
            <Button variant="secondary" disabled={!v.lo_id || aiOff} loading={drafting} onClick={() => void draftWithAi()}>
              {t("editor.aiDraft")}
            </Button>
            {aiOff && <span className="text-xs text-[color:var(--ink-2)]">{t("plan.needsAi")}</span>}
            {aiNote && <span className="flex items-center gap-2 text-xs"><DraftChip />{aiNote}</span>}
          </div>
        )}
        <label className="flex flex-col gap-1 text-sm">
          {t("editor.bloom")}
          <select className={input} value={v.bloom ?? ""} onChange={(e) => set("bloom", e.target.value || null)}>
            <option value="">—</option>
            {BLOOM.map((b) => (
              <option key={b} value={b}>{t(`editor.blooms.${b}`)}</option>
            ))}
          </select>
          {fieldError("bloom") && <span className="text-xs" style={{ color: "var(--not-yet)" }}>{fieldError("bloom")}</span>}
        </label>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="flex items-center gap-2">
          {t("editor.stem")}
          {dictation && (
            <Button variant="secondary" size="sm" aria-pressed={!!listening} onClick={toggleDictation}>
              {listening ? t("editor.dictateStop") : t("editor.dictate")}
            </Button>
          )}
        </span>
        <textarea className={`${input} min-h-20`} value={v.stem} onChange={(e) => set("stem", e.target.value)} required />
        {dictateNote && <span className="text-xs text-[color:var(--ink-2)]">{dictateNote}</span>}
      </label>

      {(v.type === "single" || v.type === "multi") && (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm">{t("editor.options")}</legend>
          {v.options.map((o, i) => (
            <div key={o.key} className="grid grid-cols-[2ch_1fr_1fr_auto] items-center gap-2">
              <span className="font-semibold">{o.key}</span>
              <input className={input} placeholder={t("editor.optionLabel")} value={o.label} onChange={(e) => setOpt(i, { label: e.target.value })} />
              <input className={input} placeholder={t("editor.misconception")} value={o.misconception} disabled={o.correct} onChange={(e) => setOpt(i, { misconception: e.target.value })} />
              <label className="flex items-center gap-1 text-sm">
                <input type={v.type === "single" ? "radio" : "checkbox"} name="correct" checked={o.correct} onChange={(e) => markCorrect(i, e.target.checked)} />
                {t("editor.correct")}
              </label>
            </div>
          ))}
          <div className="flex gap-2">
            {v.options.length < KEYS.length && (
              <Button variant="tertiary" onClick={() => set("options", [...v.options, { key: KEYS[v.options.length]!, label: "", correct: false, misconception: "" }])}>
                {t("editor.addOption")}
              </Button>
            )}
            {v.options.length > 2 && (
              <Button variant="tertiary" onClick={() => set("options", v.options.slice(0, -1))}>{t("editor.removeOption")}</Button>
            )}
          </div>
          {fieldError("options") && <p className="text-sm" style={{ color: "var(--not-yet)" }}>{fieldError("options")}</p>}
        </fieldset>
      )}

      {v.type === "ordering" && (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm">{t("editor.orderLegend")}</legend>
          <p className="text-xs text-[color:var(--ink-2)]">{t("editor.orderHint")}</p>
          {v.options.map((o, i) => (
            <div key={o.key} className="grid grid-cols-[2ch_1fr_auto_auto] items-center gap-2">
              <span className="font-semibold">{i + 1}</span>
              <input className={input} placeholder={t("editor.orderStep")} value={o.label} onChange={(e) => setOpt(i, { label: e.target.value })} />
              <Button variant="tertiary" size="sm" aria-label={t("editor.up")} disabled={i === 0} onClick={() => set("options", v.options.map((x, j) => (j === i - 1 ? v.options[i]! : j === i ? v.options[i - 1]! : x)))}>↑</Button>
              <Button variant="tertiary" size="sm" aria-label={t("editor.down")} disabled={i === v.options.length - 1} onClick={() => set("options", v.options.map((x, j) => (j === i + 1 ? v.options[i]! : j === i ? v.options[i + 1]! : x)))}>↓</Button>
            </div>
          ))}
          <div className="flex gap-2">
            {v.options.length < KEYS.length && (
              <Button variant="tertiary" onClick={() => set("options", [...v.options, { key: KEYS[v.options.length]!, label: "", correct: false, misconception: "" }])}>{t("editor.addOption")}</Button>
            )}
            {v.options.length > 2 && <Button variant="tertiary" onClick={() => set("options", v.options.slice(0, -1))}>{t("editor.removeOption")}</Button>}
          </div>
          {fieldError("answer_spec") && <p className="text-sm" style={{ color: "var(--not-yet)" }}>{fieldError("answer_spec")}</p>}
          {fieldError("options") && <p className="text-sm" style={{ color: "var(--not-yet)" }}>{fieldError("options")}</p>}
        </fieldset>
      )}

      {v.type === "hotspot" && (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm">{t("editor.regions")}</legend>
          <p className="text-xs text-[color:var(--ink-2)]">{t("editor.regionsHint")}</p>
          {v.options.map((o, i) => (
            <div key={o.key} className="grid grid-cols-[2ch_1fr_1fr_auto] items-center gap-2">
              <span className="font-semibold">{o.key}</span>
              <input className={input} placeholder={t("editor.regionLabel")} value={o.label} onChange={(e) => setOpt(i, { label: e.target.value })} />
              <label className="flex items-center gap-2 text-xs">
                {o.image ? <img src={o.image} alt={o.label || o.key} className="h-10 w-auto rounded-sm" /> : <span className="text-[color:var(--ink-2)]">{t("editor.noImage")}</span>}
                <input
                  type="file"
                  accept="image/*"
                  className="text-xs"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > MAX_IMAGE_BYTES) {
                      setError({ field: "options", detail: t("editor.imageTooBig") });
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => setOpt(i, { image: String(reader.result) });
                    reader.readAsDataURL(f);
                  }}
                />
              </label>
              <label className="flex items-center gap-1 text-sm">
                <input type="radio" name="correct" checked={o.correct} onChange={(e) => markCorrect(i, e.target.checked)} />
                {t("editor.correct")}
              </label>
            </div>
          ))}
          <div className="flex gap-2">
            {v.options.length < KEYS.length && (
              <Button variant="tertiary" onClick={() => set("options", [...v.options, { key: KEYS[v.options.length]!, label: "", correct: false, misconception: "", image: null }])}>{t("editor.addRegion")}</Button>
            )}
            {v.options.length > 2 && <Button variant="tertiary" onClick={() => set("options", v.options.slice(0, -1))}>{t("editor.removeOption")}</Button>}
          </div>
          {fieldError("options") && <p className="text-sm" style={{ color: "var(--not-yet)" }}>{fieldError("options")}</p>}
        </fieldset>
      )}

      {v.type === "numeric" && (
        <div className="grid gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            {t("editor.value")}
            <input className={input} type="number" step="any" value={spec.value ?? ""} onChange={(e) => set("answer_spec", { ...spec, value: Number(e.target.value) })} required />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            {t("editor.tolerance")}
            <input className={input} type="number" step="any" min="0" value={spec.tolerance ?? 0} onChange={(e) => set("answer_spec", { ...spec, tolerance: Number(e.target.value) })} />
          </label>
          {fieldError("answer_spec") && <p className="text-sm md:col-span-2" style={{ color: "var(--not-yet)" }}>{fieldError("answer_spec")}</p>}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-3">
        <label className="flex flex-col gap-1 text-sm">
          {t("editor.difficulty")}
          <input className={input} type="number" min="1" max="5" value={v.difficulty_authored ?? ""} onChange={(e) => set("difficulty_authored", e.target.value ? Number(e.target.value) : null)} />
          {fieldError("difficulty_authored") && <span className="text-xs" style={{ color: "var(--not-yet)" }}>{fieldError("difficulty_authored")}</span>}
        </label>
        <label className="flex flex-col gap-1 text-sm">
          {t("editor.tags")}
          <input className={input} value={(v.tags ?? []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
        </label>
        <label className="flex items-center gap-2 self-end text-sm">
          <input type="checkbox" checked={v.confidence_enabled} onChange={(e) => set("confidence_enabled", e.target.checked)} />
          {t("editor.confidence")}
        </label>
        <label className="flex items-center gap-2 self-end text-sm">
          <input type="checkbox" checked={!!v.spoken} onChange={(e) => set("spoken", e.target.checked)} />
          <span>{t("editor.spoken")} <span className="text-[color:var(--ink-2)]">{t("editor.spokenHint")}</span></span>
        </label>
      </div>

      {error && !error.field && <p role="alert" className="text-sm" style={{ color: "var(--not-yet)" }}>{error.detail}</p>}
      <div className="flex gap-2">
        <Button type="submit" loading={busy}>{item ? t("editor.save") : t("editor.create")}</Button>
        <Button variant="secondary" onClick={onCancel}>{t("editor.cancel")}</Button>
      </div>
    </form>
  );
}
